import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import cookies from 'js-cookie';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { WEGO_PREFERRED_PAYMENT_METHODS_COOKIE_KEY } from '@constants/cookie';

import { getUserState } from '@redux/selectors';

const getDefaultPreferredPaymentMethods = () => {
  try {
    return JSON.parse(cookies.get(WEGO_PREFERRED_PAYMENT_METHODS_COOKIE_KEY)!) as number[];
  } catch {
    return;
  }
};

const EMPTY_ARRAY: number[] = [];

const MAX_ALLOWED_PAYMENT_METHODS = 10;

interface UsePreferredPaymentMethodsOptions {
  pageViewId?: string;
  searchType?: string;
  wegoAnalyticsClientId?: string;
  wegoAnalyticsClientSessionId?: string;
}

const usePreferredPaymentMethods = (
  availablePaymentMethods: HomepageFlightsPaymentMethod[],
  options?: UsePreferredPaymentMethodsOptions,
) => {
  const user = useSelector(getUserState);
  const [defaultPreferredPaymentMethods, setDefaultPreferredPaymentMethods] = useState<
    number[] | undefined
  >(() => getDefaultPreferredPaymentMethods());
  const [preferredPaymentMethods, setPreferredPaymentMethods] = useState<number[] | undefined>(
    () => defaultPreferredPaymentMethods,
  );

  useEffect(() => {
    setPreferredPaymentMethods((prev) => {
      if (!prev && availablePaymentMethods.length) {
        return availablePaymentMethods
          .filter((paymentMethod) => paymentMethod.isPopular)
          .map((paymentMethod) => paymentMethod.id);
      }

      return prev;
    });
  }, [availablePaymentMethods]);

  const togglePreferredPaymentMethod = useCallback(
    (paymentMethodId: number, shouldSave?: boolean) => {
      setPreferredPaymentMethods((prev) => {
        if (!prev) return;

        let newPreferredPaymentMethods = prev;

        const isSelected = !prev.includes(paymentMethodId);

        if (!isSelected) {
          newPreferredPaymentMethods = prev.filter((id) => id !== paymentMethodId);
        } else if (prev.length < MAX_ALLOWED_PAYMENT_METHODS) {
          newPreferredPaymentMethods = [...prev, paymentMethodId];
        }

        if (shouldSave) {
          cookies.set(
            WEGO_PREFERRED_PAYMENT_METHODS_COOKIE_KEY,
            JSON.stringify(newPreferredPaymentMethods),
          );
          setDefaultPreferredPaymentMethods(newPreferredPaymentMethods);
        }

        // Track payment method selection event
        if (
          options?.wegoAnalyticsClientId &&
          options?.wegoAnalyticsClientSessionId &&
          options?.pageViewId
        ) {
          const paymentMethod = availablePaymentMethods.find((pm) => pm.id === paymentMethodId);

          // Only send event if max limit is not reached when trying to select
          if (paymentMethod && !(isSelected && prev.length >= MAX_ALLOWED_PAYMENT_METHODS)) {
            const eventData = {
              id: options.pageViewId,
              category: 'search_form_flights',
              object: options.searchType || 'roundTrip',
              action: 'payment_method',
              value: `${paymentMethod.name.replace(/ /g, '_')}_${isSelected}`,
            };

            genzoTrackActionEvent(
              API_BASE_URL,
              options.wegoAnalyticsClientId,
              options.wegoAnalyticsClientSessionId,
              user?.userHash,
              eventData,
            );
          }
        }

        return newPreferredPaymentMethods;
      });
    },
    [availablePaymentMethods, options, user?.userHash],
  );

  const savePreferredPaymentMethods = useCallback(() => {
    cookies.set(WEGO_PREFERRED_PAYMENT_METHODS_COOKIE_KEY, JSON.stringify(preferredPaymentMethods));
    setDefaultPreferredPaymentMethods(getDefaultPreferredPaymentMethods());
  }, [preferredPaymentMethods]);

  const resetPreferredPaymentMethods = useCallback(() => {
    setPreferredPaymentMethods(
      defaultPreferredPaymentMethods !== undefined
        ? defaultPreferredPaymentMethods
        : availablePaymentMethods
            .filter((paymentMethod) => paymentMethod.isPopular)
            .map((paymentMethod) => paymentMethod.id),
    );
  }, [defaultPreferredPaymentMethods, availablePaymentMethods]);

  return {
    defaultPreferredPaymentMethods,
    preferredPaymentMethods: preferredPaymentMethods || EMPTY_ARRAY,
    togglePreferredPaymentMethod,
    savePreferredPaymentMethods,
    resetPreferredPaymentMethods,
    setPreferredPaymentMethods,
  };
};

export default usePreferredPaymentMethods;
